import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BookOpen, TrendingUp, Calculator, Award, Clock, ChevronRight } from 'lucide-react';

export default function LearnHome({ user, onLogout }) {
  const navigate = useNavigate();

  const lessons = [
    {
      id: 1,
      category: 'Investment Basics',
      title: 'Why Invest? The Power of Wealth Building',
      description: 'Understand why investing is crucial for beating inflation and building long-term wealth',
      duration: '8 min',
      level: 'Beginner',
      path: '/learn/why-invest',
      icon: '💰'
    },
    {
      id: 2,
      category: 'Investment Basics',
      title: 'Understanding Risk vs Return',
      description: 'Learn the fundamental relationship between risk and returns in investing',
      duration: '10 min',
      level: 'Beginner',
      path: '/learn/risk-return',
      icon: '⚖️'
    },
    {
      id: 3,
      category: 'Investment Basics',
      title: 'The Magic of Compounding',
      description: 'Discover how compound interest can exponentially grow your wealth over time',
      duration: '12 min',
      level: 'Beginner',
      path: '/learn/compounding',
      icon: '📈'
    },
    {
      id: 4,
      category: 'Fundamental Analysis',
      title: 'Reading Financial Statements',
      description: 'Master the art of analyzing balance sheets, P&L, and cash flow statements',
      duration: '15 min',
      level: 'Intermediate',
      path: '/learn/financial-statements',
      icon: '📊'
    },
    {
      id: 5,
      category: 'Fundamental Analysis',
      title: 'Key Metrics: P/E, ROE, Debt/Equity',
      description: 'Deep dive into the most important financial ratios for stock evaluation',
      duration: '18 min',
      level: 'Intermediate',
      path: '/learn/key-metrics',
      icon: '📉'
    },
    {
      id: 6,
      category: 'Fundamental Analysis',
      title: 'Intrinsic Value & Stock Valuation',
      description: 'Learn how to calculate what a stock is actually worth using DCF and other methods',
      duration: '20 min',
      level: 'Intermediate',
      path: '/learn/intrinsic-value',
      icon: '💎'
    },
    {
      id: 7,
      category: 'Value Investing',
      title: "Warren Buffett's Investment Philosophy",
      description: 'Understand the principles that made Buffett one of the greatest investors',
      duration: '15 min',
      level: 'Advanced',
      path: '/learn/buffett-philosophy',
      icon: '🎯'
    },
    {
      id: 8,
      category: 'Value Investing',
      title: 'Margin of Safety & Long-term Thinking',
      description: 'Learn to buy with a margin of safety and think like a long-term investor',
      duration: '12 min',
      level: 'Advanced',
      path: '/learn/margin-of-safety',
      icon: '🛡️'
    }
  ];

  const calculators = [
    {
      title: 'Intrinsic Value Calculator',
      description: 'Calculate the true worth of a stock using DCF method with step-by-step explanation',
      path: '/learn/calculator/intrinsic-value',
      icon: '🧮'
    },
    {
      title: 'Stock Valuation Tool',
      description: 'Quick valuation check using multiple methods (P/E, PEG, DCF) with comparisons',
      path: '/learn/calculator/valuation',
      icon: '⚡'
    }
  ];

  const categories = [
    { name: 'Investment Basics', count: 3, color: 'from-green-400 to-emerald-500' },
    { name: 'Fundamental Analysis', count: 3, color: 'from-blue-400 to-indigo-500' },
    { name: 'Value Investing', count: 2, color: 'from-purple-400 to-pink-500' }
  ];

  return (
    <div className="min-h-screen p-4 lg:p-8">
      <Navigation user={user} onLogout={onLogout} />
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100">Learn Investing</h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400 ml-15">Master the art of investing from basics to advanced strategies</p>
        </div>

        {/* Progress Overview */}
        <Card className="glass p-6 mb-8 dark:bg-gray-800 border-l-4 border-indigo-500">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-2">Your Learning Journey</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">8 lessons • 3 interactive calculators • ~2 hours total</p>
            </div>
            <div className="flex items-center space-x-2">
              <Award className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
            </div>
          </div>
        </Card>

        {/* Categories */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {categories.map((cat, idx) => (
            <Card key={idx} className={`glass p-5 dark:bg-gray-800 bg-gradient-to-br ${cat.color} bg-opacity-10`}>
              <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">{cat.name}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">{cat.count} lessons</p>
            </Card>
          ))}
        </div>

        {/* Lessons */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6">Lessons</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {lessons.map((lesson) => (
              <Card 
                key={lesson.id} 
                className="glass p-6 dark:bg-gray-800 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(lesson.path)}
              >
                <div className="flex items-start space-x-4">
                  <div className="text-4xl flex-shrink-0">{lesson.icon}</div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        lesson.level === 'Beginner' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                        lesson.level === 'Intermediate' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
                        'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                      }`}>
                        {lesson.level}
                      </span>
                      <span className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                        <Clock className="w-3 h-3 mr-1" />
                        {lesson.duration}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-2">{lesson.title}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{lesson.description}</p>
                    <div className="flex items-center text-indigo-600 dark:text-indigo-400 text-sm font-semibold">
                      Start Learning
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Calculators */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6">Interactive Calculators</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {calculators.map((calc, idx) => (
              <Card 
                key={idx} 
                className="glass p-6 dark:bg-gray-800 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(calc.path)}
              >
                <div className="text-4xl mb-3">{calc.icon}</div>
                <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-2">{calc.title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{calc.description}</p>
                <Button variant="outline" size="sm" className="w-full dark:bg-gray-700 dark:text-gray-100">
                  <Calculator className="w-4 h-4 mr-2" />
                  Open Calculator
                </Button>
              </Card>
            ))}
          </div>
        </div>

      </div>
      <Footer />
    </div>
  );
}
